import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { api } from "services/api.service";
import TokenService from "services/token.service";

type AuthenticationContextValue = {
  loggedIn: Boolean;
  setLoggedIn: (loggedIn: Boolean) => void;
};

export type PasswordLoginResult =
  | { mfaRequired: true; mfaToken: string }
  | { mfaRequired: false };

export const AuthenticationContext = createContext<AuthenticationContextValue>({
  loggedIn: false,
  setLoggedIn: () => {},
});

export const AuthenticationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [loggedIn, setLoggedIn] = useState<Boolean>(
    Boolean(TokenService.get())
  );

  const value = useMemo(
    () => ({
      loggedIn,
      setLoggedIn,
    }),
    [loggedIn]
  );

  return (
    <AuthenticationContext.Provider value={value}>
      {children}
    </AuthenticationContext.Provider>
  );
};

function loginErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string; message?: string } | undefined;
    return data?.error || data?.message || fallback;
  }
  return fallback;
}

export default function useAuthentication() {
  const { loggedIn, setLoggedIn } = useContext(AuthenticationContext);

  const completeLogin = useCallback((jwt: string) => {
    TokenService.set(jwt);
    window.location.replace("/");
  }, []);

  const login = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      const {
        data: { jwt },
      } = await api.post("/sessions", { email, password });

      TokenService.set(jwt);
      setLoggedIn(true);
    },
    [setLoggedIn]
  );

  const loginWithEmailAndPassword = useCallback(
    async (email: string, password: string): Promise<PasswordLoginResult> => {
      try {
        const { data } = await api.post<{
          jwt?: string;
          mfaRequired?: boolean;
          mfaToken?: string;
        }>("/login", { email, password });
        if (data.mfaRequired && data.mfaToken) {
          return { mfaRequired: true, mfaToken: data.mfaToken };
        }
        if (data.jwt) {
          completeLogin(data.jwt);
          return { mfaRequired: false };
        }
        throw new Error("Invalid email or password");
      } catch (error: unknown) {
        console.error("Error logging in:", error);
        throw new Error(loginErrorMessage(error, "Invalid email or password"));
      }
    },
    [completeLogin]
  );

  const verifyMfa = useCallback(
    async (mfaToken: string, code: string) => {
      try {
        const { data } = await api.post<{ jwt?: string }>("/verify_mfa", {
          mfaToken,
          code,
        });
        if (data.jwt) {
          completeLogin(data.jwt);
          return;
        }
        throw new Error("Invalid verification code");
      } catch (error: unknown) {
        throw new Error(loginErrorMessage(error, "Invalid verification code"));
      }
    },
    [completeLogin]
  );

  const resendMfa = useCallback(async (mfaToken: string) => {
    try {
      const { data } = await api.post<{ mfaToken?: string }>("/resend_mfa", {
        mfaToken,
        challenge: mfaToken,
      });
      return data.mfaToken || mfaToken;
    } catch (error: unknown) {
      throw new Error(loginErrorMessage(error, "Unable to resend verification code"));
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      await api.post("/reset_password", { email });
      window.location.href = "/check-email";
    } catch (error) {
      console.error("Error resetting password:", error);
      throw error;
    }
  }, []);

  const setPassword = useCallback(
    (token: string, password: string) => {
      api
        .post("/set_password", { token, password })
        .then(({ data: { jwt } }) => {
          if (jwt) {
            completeLogin(jwt);
          }
        })
        .catch((error) => {
          console.error("Error in setPassword:", error);
        });
    },
    [completeLogin]
  );

  return {
    login,
    loggedIn,
    loginWithEmailAndPassword,
    verifyMfa,
    resendMfa,
    resetPassword,
    setPassword,
  };
}
